"use client";

import { useState } from "react";
import Image from "next/image";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { Workflow, Copy, Check, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Link from "next/link";

const exampleData = {
  image: "/images/robbery_example.jpeg",
  prompt: "what is happening?",
  response: `{
  "answer": "A man is trying to break into a white car"
}`,
  responseTimeMs: 485,
  inputTokens: 729,
  outputTokens: 12,
};

const codeExamples = {
  python: `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load your image
image = Image.open("security_camera.jpg")

# Ask a question about the image
result = model.query(image, "what is happening?")
print(result["answer"])`,

  nodejs: `import { vl } from 'moondream';
import fs from 'fs';

// Initialize with API key
const model = new vl({ apiKey: "your-api-key" });

// Load your image
const image = fs.readFileSync("security_camera.jpg");

// Ask a question about the image
const result = await model.query({
  image: image,
  question: "what is happening?"
});
console.log(result.answer);`,

  curl: `curl -X POST https://api.moondream.ai/v1/query \\
  -H 'Content-Type: application/json' \\
  -H 'X-Moondream-Auth: YOUR_API_KEY' \\
  -d '{
    "image_url": "data:image/jpeg;base64,...",
    "question": "what is happening?"
  }'`,
};

export function HowItWorks() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(key);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const calculateCost = (inputTokens: number, outputTokens: number): string => {
    const inputCost = (inputTokens / 1_000_000) * 0.3;
    const outputCost = (outputTokens / 1_000_000) * 2.5;
    const totalCost = inputCost + outputCost;
    return `$${totalCost.toFixed(6)}`;
  };

  const playgroundLink = `/playground?image=${encodeURIComponent(exampleData.image)}&prompt=${encodeURIComponent(exampleData.prompt)}&skill=query`;

  return (
    <PageContent>
      <div className="space-y-6">
        <SectionHeader
          sectionIcon={<Workflow className="size-4" />}
          section="How it works"
          heading="How query works"
          description="Ask any natural language question about an image and get detailed, accurate answers."
        />

        <Card className="overflow-hidden">
          <CardContent className="p-6">
            {/* Image + Prompt/Response Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Image */}
              <div className="lg:w-1/2 shrink-0">
                <Image
                  src={exampleData.image}
                  alt="Security camera footage"
                  width={500}
                  height={400}
                  className="rounded-lg border w-full object-cover"
                />
              </div>

              {/* Prompt and Response */}
              <div className="lg:w-1/2 flex flex-col gap-4">
                {/* Prompt */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Query
                    </h3>
                    <Button asChild size="sm">
                      <Link href={playgroundLink}>
                        Try it
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                  <Card className="bg-muted/50">
                    <CardContent className="p-4">
                      <p className="text-sm font-mono">&quot;{exampleData.prompt}&quot;</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Response */}
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Output
                    </h3>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {exampleData.responseTimeMs}ms •{" "}
                      {exampleData.inputTokens + exampleData.outputTokens} tokens •{" "}
                      {calculateCost(exampleData.inputTokens, exampleData.outputTokens)}
                    </div>
                  </div>
                  <Card className="bg-muted/50 flex-1">
                    <CardContent className="p-4">
                      <pre className="text-sm whitespace-pre-wrap font-mono">
                        {exampleData.response}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Code Examples */}
            <div className="mt-6 space-y-2">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Code
              </h3>
              <Tabs defaultValue="python" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="python">Python</TabsTrigger>
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                </TabsList>

                {(["python", "nodejs", "curl"] as const).map((lang) => {
                  const code = codeExamples[lang];
                  const copyKey = `how-it-works-${lang}`;

                  return (
                    <TabsContent key={lang} value={lang} className="mt-4">
                      <div className="relative">
                        <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm border">
                          <code
                            className={`language-${lang === "nodejs" ? "javascript" : lang === "curl" ? "bash" : lang}`}
                          >
                            {code}
                          </code>
                        </pre>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 h-8 w-8 p-0"
                          onClick={() => copyToClipboard(code, copyKey)}
                        >
                          {copiedCode === copyKey ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContent>
  );
}
